import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  organizationConfigService,
  type OrgConfigChangeStatus,
  type UpdateCreditSettingsData,
  type UpdateTransactionLimitsData,
  type UpdatePayrollConfigData,
} from '@/services/organizationConfig.service';

// Query key factories
export const orgConfigKeys = {
  all: ['organizationConfig'] as const,
  credit: () => [...orgConfigKeys.all, 'credit'] as const,
  creditStatus: () => [...orgConfigKeys.all, 'credit', 'status'] as const,
  limits: () => [...orgConfigKeys.all, 'limits'] as const,
  limitsStatus: () => [...orgConfigKeys.all, 'limits', 'status'] as const,
  payroll: () => [...orgConfigKeys.all, 'payroll'] as const,
  payrollStatus: () => [...orgConfigKeys.all, 'payroll', 'status'] as const,
  payableSummary: (payDate?: string) =>
    [...orgConfigKeys.all, 'payroll', 'payable', payDate ?? 'next'] as const,
  changes: (params?: { status?: OrgConfigChangeStatus; organizationId?: string }) =>
    [...orgConfigKeys.all, 'changes', params ?? {}] as const,
};

// ==================== CREDIT SETTINGS ====================

export function useCreditSettings() {
  return useQuery({
    queryKey: orgConfigKeys.credit(),
    queryFn: () => organizationConfigService.getCreditSettings(),
  });
}

export function useCreditSettingsStatus() {
  return useQuery({
    queryKey: orgConfigKeys.creditStatus(),
    queryFn: () => organizationConfigService.getCreditSettingsStatus(),
  });
}

export function useUpdateCreditSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateCreditSettingsData) =>
      organizationConfigService.updateCreditSettings(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgConfigKeys.credit() });
      void queryClient.invalidateQueries({ queryKey: orgConfigKeys.creditStatus() });
    },
  });
}

// ==================== TRANSACTION LIMITS ====================

export function useTransactionLimits() {
  return useQuery({
    queryKey: orgConfigKeys.limits(),
    queryFn: () => organizationConfigService.getTransactionLimits(),
  });
}

export function useTransactionLimitsStatus() {
  return useQuery({
    queryKey: orgConfigKeys.limitsStatus(),
    queryFn: () => organizationConfigService.getTransactionLimitsStatus(),
  });
}

export function useUpdateTransactionLimits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTransactionLimitsData) =>
      organizationConfigService.updateTransactionLimits(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgConfigKeys.limits() });
      void queryClient.invalidateQueries({ queryKey: orgConfigKeys.limitsStatus() });
    },
  });
}

// ==================== PAYROLL CONFIG ====================

export function usePayrollConfig() {
  return useQuery({
    queryKey: orgConfigKeys.payroll(),
    queryFn: () => organizationConfigService.getPayrollConfig(),
  });
}

export function usePayrollConfigStatus() {
  return useQuery({
    queryKey: orgConfigKeys.payrollStatus(),
    queryFn: () => organizationConfigService.getPayrollConfigStatus(),
  });
}

export function usePayableSummary(payDate?: string) {
  return useQuery({
    queryKey: orgConfigKeys.payableSummary(payDate),
    queryFn: () => organizationConfigService.getPayableSummary(payDate),
  });
}

export function useUpdatePayrollConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePayrollConfigData) =>
      organizationConfigService.updatePayrollConfig(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: orgConfigKeys.payroll() });
      void queryClient.invalidateQueries({ queryKey: orgConfigKeys.payrollStatus() });
    },
  });
}

// ==================== CONFIG CHANGE APPROVAL (super admin) ====================

export function useConfigChanges(params?: {
  status?: OrgConfigChangeStatus;
  organizationId?: string;
}) {
  return useQuery({
    queryKey: orgConfigKeys.changes(params),
    queryFn: () => organizationConfigService.listConfigChanges(params),
  });
}

export function useApproveConfigChange() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => organizationConfigService.approveConfigChange(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...orgConfigKeys.all, 'changes'],
      });
    },
  });
}

export function useRejectConfigChange() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => organizationConfigService.rejectConfigChange(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...orgConfigKeys.all, 'changes'],
      });
    },
  });
}
